import React from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import format from 'string-template';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { confirmation, length } from 'redux-form-validators';

import {
    EBON_MESSAGE_LENGTH,
    FIRSTNAME_LENGTH,
    NAME_LENGTH,
    PHONE_NUMBER_LENGTH_MIN,
    PHONE_NUMBER_LENGTH_MAX,
    CITY_LENGTH,
    ADDRESS_LENGTH,
    ZIP_CODE_LENGTH
} from '../../../constants/form';

import { convertHtmlToReactDomWithParams } from '../../../utils/htmlUtils';

import {
    EmailInputFieldComponent,
    FormComponent,
    NumberInputFieldComponent,
    PhoneInputFieldComponent,
    SelectFieldComponent,
    TextareaFieldComponent,
    TextInputFieldComponent
} from '../../form';
import Tooltip from '../../tooltip/Tooltip';
import LocalizedNavLink from '../../router/LocalizedNavLink';

const mapCountries = countries => {
    if (!isEmpty(countries)) {
        return Object.values(countries).map(country => {
            return {
                label: country.label,
                value: country.value
            };
        });
    }
    return {};
};

const AddressForm = ({
    formName,
    cappingError,
    initialValues,
    isFetching,
    handleSubmit,
    merchantCardPath,
    i18n,
    i18nCommon,
    COUNTRIES
}) => {
    return (
        <FormComponent
            form={formName}
            initialValues={{
                lastname: initialValues.lastname,
                firstname: initialValues.firstname,
                address: initialValues.street,
                zipCode: initialValues.postcode,
                city: initialValues.city,
                country: initialValues.country ? initialValues.country : 'FR',
                email: initialValues.email,
                emailConfirm: initialValues.email,
                phoneNumber: initialValues.phoneNumber
            }}
            classNameInputContainer="grid-1 has-gutter"
            requestSubmit={isFetching}
            onSubmit={() => handleSubmit()}
            submitText={i18nCommon.VALIDATE_USER_DETAILS}
            paymentLockLabel={i18nCommon.PAYMENT_LOCKPAD_LABEL}>
            <React.Fragment>
                <h3 className="full">{i18nCommon.DELIVERY_ADDRESS_TITLE}</h3>
            </React.Fragment>

            <TextInputFieldComponent
                name="lastname"
                i18nFeatureForm={i18nCommon}
                placeholder={i18nCommon.LABEL_LASTNAME}
                label={i18nCommon.LABEL_LASTNAME}
                isRequired
                classNameInputContainer="one-half"
                validate={[
                    length({
                        max: NAME_LENGTH,
                        msg: format(
                            i18nCommon.TEXT_TOOLONG,
                            NAME_LENGTH,
                            NAME_LENGTH > 1 ? 's' : ''
                        )
                    })
                ]}
            />
            <TextInputFieldComponent
                name="firstname"
                i18nFeatureForm={i18nCommon}
                placeholder={i18nCommon.LABEL_FIRSTNAME}
                label={i18nCommon.LABEL_FIRSTNAME}
                isRequired
                classNameInputContainer="one-half"
                validate={[
                    length({
                        max: FIRSTNAME_LENGTH,
                        msg: format(
                            i18nCommon.TEXT_TOOLONG,
                            FIRSTNAME_LENGTH,
                            FIRSTNAME_LENGTH > 1 ? 's' : ''
                        )
                    })
                ]}
            />
            <TextInputFieldComponent
                name="address"
                i18nFeatureForm={i18nCommon}
                classNameInputContainer="full"
                placeholder={i18nCommon.LABEL_ADDRESS}
                label={i18nCommon.LABEL_ADDRESS}
                isRequired
                validate={[
                    length({
                        max: ADDRESS_LENGTH,
                        msg: i18nCommon.ADDRESS_SIZE_ERROR
                    })
                ]}
            />
            <NumberInputFieldComponent
                name="zipCode"
                i18nFeatureForm={i18nCommon}
                classNameInputContainer="one-half"
                placeholder={i18nCommon.LABEL_ZIPCODE}
                label={i18nCommon.LABEL_ZIPCODE}
                isRequired
                validate={[
                    length({
                        max: ZIP_CODE_LENGTH,
                        msg: i18nCommon.ZIP_CODE_SIZE_ERROR
                    })
                ]}
            />
            <TextInputFieldComponent
                name="city"
                i18nFeatureForm={i18nCommon}
                classNameInputContainer="one-half"
                placeholder={i18nCommon.LABEL_CITY}
                label={i18nCommon.LABEL_CITY}
                isRequired
                validate={[
                    length({
                        max: CITY_LENGTH,
                        msg: i18nCommon.CITY_SIZE_ERROR
                    })
                ]}
            />
            <SelectFieldComponent
                name="country"
                i18nFeatureForm={i18nCommon}
                label={i18nCommon.LABEL_COUNTRY}
                isRequired
                defaultOptionLabel={i18nCommon.LABEL_COUNTRY}
                classNameInputContainer="full"
                options={COUNTRIES}
            />
            <EmailInputFieldComponent
                name="email"
                classNameInputContainer="full"
                i18nFeatureForm={i18nCommon}
                placeholder={i18nCommon.PLACEHOLDER_EMAIL}
                label={i18nCommon.LABEL_EMAIL}
                isRequired
            />
            <EmailInputFieldComponent
                name="emailConfirm"
                classNameInputContainer="full"
                placeholder={i18nCommon.PLACEHOLDER_EMAIL_CONFIRM}
                label={i18nCommon.LABEL_EMAIL_CONFIRM}
                i18nFeatureForm={i18nCommon}
                isRequired
                validate={[
                    confirmation({
                        field: 'email',
                        msg: i18nCommon.EMAIL_CONFIRM_ERROR
                    })
                ]}
            />
            <PhoneInputFieldComponent
                name="phoneNumber"
                label={i18nCommon.LABEL_PHONENUMBER}
                labelInfo={
                    <Tooltip text={i18n.PHONE_NUMBER_INFO} right isHover>
                        {i18n.PHONE_NUMBER_LABEL_INFO}
                    </Tooltip>
                }
                i18nFeatureForm={i18nCommon}
                classNameInputContainer="full"
                isRequired
                validate={[
                    length({
                        max: PHONE_NUMBER_LENGTH_MAX,
                        msg: format(i18nCommon.PHONENUMBER_MAX_SIZE_ERROR, PHONE_NUMBER_LENGTH_MAX)
                    }),
                    length({
                        min: PHONE_NUMBER_LENGTH_MIN,
                        msg: i18nCommon.PHONENUMBER_MIN_SIZE_ERROR
                    })
                ]}
            />
            <TextareaFieldComponent
                name="message"
                label={i18nCommon.LABEL_USER_MESSAGE}
                classNameInputContainer="full"
                validate={[
                    length({
                        max: EBON_MESSAGE_LENGTH
                    })
                ]}
            />

            <React.Fragment>
                {cappingError && (
                    <p className="error-message">
                        {convertHtmlToReactDomWithParams(i18n.CAPPING_ERROR_MESSAGE_1)}
                        <LocalizedNavLink
                            to={`${merchantCardPath}#conditions`}
                            title={i18n.CAPPING_ERROR_MESSAGE_2}>
                            {i18n.CAPPING_ERROR_MESSAGE_2}
                        </LocalizedNavLink>
                        {i18n.CAPPING_ERROR_MESSAGE_3}
                    </p>
                )}
            </React.Fragment>
        </FormComponent>
    );
};

AddressForm.propTypes = {
    formName: PropTypes.string,
    cappingError: PropTypes.bool,
    initialValues: PropTypes.object,
    isFetching: PropTypes.bool,
    handleSubmit: PropTypes.func,
    merchantCardPath: PropTypes.string
};

const mapStateToProps = state => {
    return {
        i18n: state.i18n.page.EBON,
        i18nCommon: state.i18n.feature.form,
        COUNTRIES: mapCountries(state.i18n.feature.form.COUNTRIES)
    };
};

export default withRouter(connect(mapStateToProps)(AddressForm));



// WEBPACK FOOTER //
// ./src/components/funnel/address-step/AddressForm.js